// Push Notification Utilities for High Achievers
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return { success: false, message: 'Push notifications not supported in this browser' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { 
      success: permission === 'granted', 
      permission,
      message: permission === 'granted' ? 'Notifications enabled!' : 'Notification permission denied'
    };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, message: error.message };
  }
}

// Subscribe to push notifications
export async function subscribeToPush(token) {
  if (!isPushSupported()) {
    return { success: false, message: 'Push notifications not supported' };
  }
  
  try {
    // Get VAPID public key from server
    const keyResponse = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
    const { publicKey } = await keyResponse.json();
    
    if (!publicKey) {
      return { success: false, message: 'Push notifications not configured on server' };
    }
    
    // Register service worker if not already registered
    const registration = await navigator.serviceWorker.ready;
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
    
    // Send subscription to server
    const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      })
    });
    
    if (response.ok) {
      return { success: true, message: 'Subscribed to notifications!' };
    } else {
      const error = await response.json();
      return { success: false, message: error.detail || 'Failed to subscribe' };
    }
  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, message: error.message };
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(token) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      // Remove from server
      await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {}
        })
      });
    }
    
    return { success: true, message: 'Unsubscribed from notifications' };
  } catch (error) {
    console.error('Push unsubscribe error:', error);
    return { success: false, message: error.message };
  }
}

// Check if currently subscribed
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    return false;
  }
}
